import { createRouter, createWebHistory } from 'vue-router'
import { getContestEnabled } from '@/api'

const routes = [
  // 公共页面
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue')
  },
  {
    path: '/auth',
    redirect: to => {
      const path = to.query.mode === 'register' ? '/register' : '/login'
      const query = to.query.redirect ? { redirect: to.query.redirect } : {}
      return { path, query }
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue')
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/Register.vue')
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPassword.vue')
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPassword.vue')
  },
  {
    path: '/auth/wechat/callback',
    redirect: to => {
      if (typeof window !== 'undefined' && localStorage.getItem('wechatAction') === 'bind') {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
        const role = currentUser.role
        const profilePath = role === 'ADMIN' ? '/admin/profile'
          : role === 'TEACHER' ? '/teacher/profile'
          : '/student/profile'
        return { path: profilePath, query: { wechatCode: to.query.code } }
      }
      return { path: '/login', query: { code: to.query.code, state: to.query.state } }
    }
  },
  {
    path: '/auth/gitcode/callback',
    redirect: to => {
      if (typeof window !== 'undefined' && localStorage.getItem('gitcodeAction') === 'bind') {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
        const role = currentUser.role
        const profilePath = role === 'ADMIN' ? '/admin/profile'
          : role === 'TEACHER' ? '/teacher/profile'
          : '/student/profile'
        return { path: profilePath, query: { gitcodeCode: to.query.code } }
      }
      return { path: '/login', query: { gitcode_code: to.query.code, state: to.query.state } }
    }
  },
  {
    path: '/video/:type',
    name: 'VideoPlayer',
    component: () => import('@/views/VideoPlayer.vue')
  },
  // 学生页面
  {
    path: '/student/class',
    name: 'StudentClass',
    component: () => import('@/views/student/StudentClass.vue'),
    meta: { requiresAuth: true, role: 'STUDENT' }
  },
  {
    path: '/student/profile',
    name: 'StudentProfile',
    component: () => import('@/views/student/StudentProfile.vue'),
    meta: { requiresAuth: true, role: 'STUDENT' }
  },
  {
    path: '/student/announcement',
    name: 'StudentAnnouncementCenter',
    component: () => import('@/views/shared/ContestAnnouncementCenter.vue'),
    props: { role: 'STUDENT' },
    meta: { requiresAuth: true, role: 'STUDENT' }
  },
  {
    path: '/student/announcement/:announcementId',
    name: 'StudentAnnouncementDetail',
    component: () => import('@/views/student/StudentContestAnnouncementDetail.vue'),
    meta: { requiresAuth: true, role: 'STUDENT' }
  },
  {
    path: '/student/class/:classId',
    name: 'StudentClassDetail',
    component: () => import('@/views/student/StudentClassDetail.vue'),
    meta: { requiresAuth: true, role: 'STUDENT' }
  },
  {
    path: '/student/task',
    name: 'StudentTask',
    component: () => import('@/views/student/StudentTask.vue'),
    meta: { requiresAuth: true, role: 'STUDENT' }
  },
  {
    path: '/student/task/:taskId',
    name: 'StudentTaskDetail',
    component: () => import('@/views/student/StudentTaskDetail.vue'),
    meta: { requiresAuth: true, role: 'STUDENT' }
  },
  {
    path: '/student/contest',
    name: 'StudentContest',
    component: () => import('@/views/student/StudentContest.vue'),
    meta: { requiresAuth: true, role: 'STUDENT', requiresContest: true }
  },
  {
    path: '/student/contest/announcement/:announcementId',
    name: 'StudentContestAnnouncementDetail',
    component: () => import('@/views/student/StudentContestAnnouncementDetail.vue'),
    meta: { requiresAuth: true, role: 'STUDENT', requiresContest: true }
  },
  {
    path: '/student/contest/:contestId',
    name: 'StudentContestDetail',
    component: () => import('@/views/student/StudentContestDetail.vue'),
    meta: { requiresAuth: true, role: 'STUDENT', requiresContest: true }
  },

  // 老师页面
  {
    path: '/teacher/class',
    name: 'TeacherClass',
    component: () => import('@/views/teacher/TeacherClass.vue'),
    meta: { requiresAuth: true, role: 'TEACHER' }
  },
  {
    path: '/teacher/profile',
    name: 'TeacherProfile',
    component: () => import('@/views/teacher/TeacherProfile.vue'),
    meta: { requiresAuth: true, role: 'TEACHER' }
  },
  {
    path: '/teacher/announcement',
    name: 'TeacherAnnouncementCenter',
    component: () => import('@/views/shared/ContestAnnouncementCenter.vue'),
    props: { role: 'TEACHER' },
    meta: { requiresAuth: true, role: 'TEACHER' }
  },
  {
    path: '/teacher/announcement/:announcementId',
    name: 'TeacherAnnouncementDetail',
    component: () => import('@/views/teacher/TeacherContestAnnouncementDetail.vue'),
    meta: { requiresAuth: true, role: 'TEACHER' }
  },
  {
    path: '/teacher/create-class',
    name: 'TeacherCreateClass',
    component: () => import('@/views/teacher/TeacherCreateClass.vue'),
    meta: { requiresAuth: true, role: 'TEACHER' }
  },
  {
    path: '/teacher/class/:classId/edit',
    name: 'TeacherEditClass',
    component: () => import('@/views/teacher/TeacherEditClass.vue'),
    meta: { requiresAuth: true, role: 'TEACHER' }
  },
  {
    path: '/teacher/class/:classId',
    name: 'TeacherClassDetail',
    component: () => import('@/views/teacher/TeacherClassDetail.vue'),
    meta: { requiresAuth: true, role: 'TEACHER' }
  },
  {
    path: '/teacher/task',
    name: 'TeacherTask',
    component: () => import('@/views/teacher/TeacherTask.vue'),
    meta: { requiresAuth: true, role: 'TEACHER' }
  },
  {
    path: '/teacher/task/:taskId',
    name: 'TeacherTaskDetail',
    component: () => import('@/views/teacher/TeacherTaskDetail.vue'),
    meta: { requiresAuth: true, role: 'TEACHER' }
  },
  {
    path: '/teacher/project',
    name: 'TeacherProject',
    component: () => import('@/views/teacher/TeacherProject.vue'),
    meta: { requiresAuth: true, role: 'TEACHER' }
  },
  {
    path: '/teacher/problem/:problemId',
    name: 'TeacherProblemDetail',
    component: () => import('@/views/teacher/TeacherProblemDetail.vue'),
    meta: { requiresAuth: true, role: 'TEACHER' }
  },
  {
    path: '/teacher/contest',
    name: 'TeacherContest',
    component: () => import('@/views/teacher/TeacherContest.vue'),
    meta: { requiresAuth: true, role: 'TEACHER', requiresContest: true }
  },
  {
    path: '/teacher/contest/announcement/:announcementId',
    name: 'TeacherContestAnnouncementDetail',
    component: () => import('@/views/teacher/TeacherContestAnnouncementDetail.vue'),
    meta: { requiresAuth: true, role: 'TEACHER', requiresContest: true }
  },

  // 管理员页面
  {
    path: '/admin/user',
    name: 'AdminUser',
    component: () => import('@/views/admin/AdminUser.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  },
  {
    path: '/admin/mu-coin-records',
    name: 'AdminMuCoinRecords',
    component: () => import('@/views/admin/AdminMuCoinRecords.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  },
  {
    path: '/admin/profile',
    name: 'AdminProfile',
    component: () => import('@/views/admin/AdminProfile.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  },
  {
    path: '/admin/contest',
    name: 'AdminContest',
    component: () => import('@/views/admin/AdminContest.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  },
  {
    path: '/admin/contest/:contestId',
    name: 'AdminContestDetail',
    component: () => import('@/views/admin/AdminContestDetail.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  },
  {
    path: '/admin/class',
    name: 'AdminClass',
    component: () => import('@/views/admin/AdminClass.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  },
  {
    path: '/admin/class/:classId',
    name: 'AdminClassDetail',
    component: () => import('@/views/admin/AdminClassDetail.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  },
  {
    path: '/admin/task',
    name: 'AdminTask',
    component: () => import('@/views/admin/AdminTask.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  },
  {
    path: '/admin/task/:taskId',
    name: 'AdminTaskDetail',
    component: () => import('@/views/admin/AdminTaskDetail.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  },
  {
    path: '/admin/project',
    name: 'AdminProject',
    component: () => import('@/views/admin/AdminProject.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  },
  {
    path: '/admin/project/:problemId',
    name: 'AdminProblemDetail',
    component: () => import('@/views/teacher/TeacherProblemDetail.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  },
  {
    path: '/admin/system',
    name: 'AdminSystemConfig',
    component: () => import('@/views/admin/AdminSystemConfig.vue'),
    meta: { requiresAuth: true, role: 'ADMIN' }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 路由守卫
router.beforeEach(async (to, from, next) => {
  const currentUser = localStorage.getItem('currentUser')
  const user = currentUser ? JSON.parse(currentUser) : null

  if (to.meta.requiresAuth) {
    if (!user) {
      // 未登录，跳转到登录页
      next({ path: '/login', query: { redirect: to.fullPath } })
      return
    }

    // 检查测试大赛开关
    if (to.meta.requiresContest) {
      try {
        const res = await getContestEnabled()
        const data = res?.data || res
        if (!data.contestEnabled) {
          // 测试大赛未开启，重定向到对应角色的首页
          const role = user.role
          if (role === 'TEACHER') {
            next({ path: '/teacher/class' })
          } else {
            next({ path: '/student/class' })
          }
          return
        }
      } catch (e) {
        // 请求失败时也阻止访问
        const role = user.role
        if (role === 'TEACHER') {
          next({ path: '/teacher/class' })
        } else {
          next({ path: '/student/class' })
        }
        return
      }
    }

    next()
  } else {
    next()
  }
})

export default router
